import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { db } from '../config/firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';

const PaymentPending: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, userType } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'error'>('pending');

  const empresaId = searchParams.get('empresaId');
  const preferenceId = searchParams.get('preference_id');

  useEffect(() => {
    if (!isAuthenticated || userType !== 'Empresa') {
      navigate('/login');
      return;
    }

    if (!preferenceId) {
      setPaymentStatus('error');
      return;
    }

    // Monitorar status do pagamento em tempo real
    const transacoesRef = collection(db, 'transacoesCarteira');
    const q = query(
      transacoesRef,
      where('referenciaPagamento', '==', preferenceId),
      where('empresaId', '==', empresaId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const transacao = snapshot.docs[0].data();
        if (transacao.status === 'aprovado') {
          setPaymentStatus('success');
          // Redirecionar após 2 segundos
          setTimeout(() => {
            navigate('/payment/success', { 
              replace: true,
              state: { empresaId, preference_id: preferenceId }
            });
          }, 2000);
        } else if (transacao.status === 'rejeitado') {
          setPaymentStatus('error');
        }
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, userType, empresaId, preferenceId, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center">
          {paymentStatus === 'pending' && (
            <>
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Aguardando Pagamento</h1>
              <p className="text-gray-600 mb-6">
                Estamos aguardando a confirmação do seu pagamento PIX. Isso pode levar alguns minutos.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Dica:</strong> Mantenha esta página aberta. Você será redirecionado automaticamente quando o pagamento for confirmado.
                </p>
              </div>
            </>
          )}

          {paymentStatus === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Confirmado!</h1>
              <p className="text-gray-600 mb-6">
                Redirecionando...
              </p>
            </>
          )}

          {paymentStatus === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagamento Rejeitado</h1>
              <p className="text-gray-600 mb-6">
                Seu pagamento foi rejeitado. Tente novamente ou entre em contato com o suporte.
              </p>
            </>
          )}

          <button
            onClick={() => navigate('/empresa')}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPending;


